import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { workflows } from "@couragemfg/api/workflow";
import { api, type RecordDetail } from "../api";
import { useLoad } from "../hooks";
import { AccountRedirect } from "./Accounts";
import { CommandBar } from "../Shell";
import { ERP_ENTITIES, ErrorBox, FLOWS, Severity, StatePill, ago, label } from "../ui";
import { NumberCard } from "../analytics/NumberCard";
import { ProcessPicker, type ProcessValue } from "../improve/ProcessPicker";

function ProcessFlow({ rec, onMove, busy }: { rec: RecordDetail; onMove: (to: string) => void; busy: boolean }) {
  const stages = FLOWS[rec.type];
  const allowed = workflows[rec.type]?.transitions[rec.state] ?? [];
  const escalated = rec.state === "escalated";
  const cancelled = rec.state === "cancelled";
  const idx = escalated ? 1 : stages.findIndex((s) => s.id === rec.state);
  const enteredAt = (id: string) => [...rec.history].reverse().find((h) => h.toState === id)?.at;

  return (
    <div className="bpf" role="list" aria-label="Business process">
      {stages.map((s, i) => {
        const done = i < idx || (rec.state === "closed" && i === idx);
        const current = i === idx && !escalated && !cancelled && rec.state !== "closed";
        const canGo = !busy && allowed.includes(s.id) && !(rec.type === "issue" && s.id === "triaged");
        const when = enteredAt(s.id);
        const cls = current ? "current" : done ? "done" : canGo ? "next" : "";
        return (
          <button key={s.id} role="listitem" className={`bpf-stage ${cls}`} disabled={!canGo} onClick={() => canGo && onMove(s.id)}
            title={canGo ? `Move to ${s.name}` : s.name} aria-current={current ? "step" : undefined}>
            <span className="name">{done ? "✓ " : ""}{s.name}</span>
            <span className="meta">{current ? (when ? `Active · ${ago(when)}` : "Active") : done && when ? ago(when) : canGo ? "Move here" : ""}</span>
          </button>
        );
      })}
      {(escalated || cancelled) && (
        <span className="bpf-stage branch" role="listitem"><span className="name">{escalated ? "Escalated to CAPA" : "Cancelled"}</span>
          <span className="meta">{ago(enteredAt(rec.state) ?? rec.updatedAt)}</span></span>
      )}
    </div>
  );
}

export function RecordPage() {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const { data: rec, error, reload } = useLoad(() => api.record(id), [id]);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<unknown>(null);
  const [severity, setSeverity] = useState("medium");
  const [owner, setOwner] = useState("");
  const [note, setNote] = useState("");
  const [proc, setProc] = useState<ProcessValue>({ process: "", step: "" });
  const [editProc, setEditProc] = useState(false);

  if (error) return <div className="page"><ErrorBox error={error} /></div>;
  if (!rec) return <div className="page"><div className="empty">Loading…</div></div>;
  if ((rec.type as string) === "account") return <AccountRedirect number={rec.number} />;

  const d = rec.detail ?? {};
  const allowed = workflows[rec.type]?.transitions[rec.state] ?? [];
  const run = async (fn: () => Promise<any>, after?: (r: any) => void) => {
    setBusy(true); setActionError(null);
    try { const r = await fn(); setNote(""); after ? after(r) : await reload(); } catch (e) { setActionError(e); } finally { setBusy(false); }
  };
  const move = (to: string) => run(() => api.transition(rec.id, to, note.trim() || undefined));
  const canTriage = rec.type === "issue" && rec.state === "new";
  const canEscalate = rec.type === "issue" && allowed.includes("escalated");
  // Off-path moves (the process bar covers the main path): cancel a CAPA, or close a new issue without triage.
  const side = allowed.filter((s) => s === "cancelled" || (rec.type === "issue" && rec.state === "new" && s === "closed"));
  const p = rec.process ?? null;
  const improveLink = `/factory/new?issue=${rec.number}${p ? `&process=${p.number}${p.step ? `&step=${p.step.id}` : ""}` : ""}`;
  const related = [...rec.related.outgoing, ...rec.related.incoming.map((r) => ({ ...r, relation: r.relation === "escalated_to" ? "escalated_from" : r.relation }))];

  return (
    <>
      <CommandBar status={`Updated ${ago(rec.updatedAt)}`}>
        <Link className="cmd" to={rec.type === "issue" ? "/issues" : "/capas"}>← {rec.type === "issue" ? "Issues" : "CAPAs"}</Link>
        {canEscalate && <button className="cmd primary" disabled={busy} onClick={() => run(() => api.escalate(rec.id, rec.title), (c) => nav(`/r/${c.number}`))}>Escalate to CAPA</button>}
        {rec.type === "issue" && !["closed", "escalated"].includes(rec.state) && <Link className="cmd" to={improveLink}>Improve this process</Link>}
        {side.map((s) => <button key={s} className="cmd" disabled={busy} onClick={() => move(s)}>{s === "cancelled" ? "Cancel CAPA" : `Close ${rec.type === "issue" ? "issue" : "CAPA"}`}</button>)}
        <button className="cmd" onClick={() => reload()}>Refresh</button>
      </CommandBar>

      <div className="page">
        <div className="page-title" style={{ marginBottom: 12 }}>
          <span className="mono" style={{ color: "var(--fg-3)" }}>{rec.type === "issue" ? "Issue" : "CAPA"} · {rec.number}</span>
          <h1 style={{ flexBasis: "100%" }}>{rec.title}</h1>
          <StatePill state={rec.state} /><Severity severity={d.severity} /><span className="muted">{label(d.category ?? d.source)}</span>
          {rec.type === "issue" && (p
            ? <span className="muted">· <Link to={`/improve/catalog/${p.number}`}>{p.number} {p.name}</Link>{p.step && <>{" · "}{p.mapId ? <Link to={`/processes/${p.mapId}`}>Step: {p.step.name}</Link> : `Step: ${p.step.name}`}</>}</span>
            : <span className="pill warn sm">No process yet</span>)}
        </div>

        <ProcessFlow rec={rec} onMove={move} busy={busy} />
        <ErrorBox error={actionError} />

        <div className="grid cols-main">
          <div className="stack">
            <NumberCard record={rec.id} />
            {canTriage && (
              <section className="card">
                <div className="card-head"><h2>Triage</h2><span className="muted" style={{ fontSize: 12 }}>Moves this issue to Triaged</span></div>
                {!p && <div className="card-body" style={{ paddingBottom: 0 }}><ProcessPicker value={proc} onChange={setProc} required idPrefix="tr" /></div>}
                <div className="card-body btn-row">
                  <label className="sr-only" htmlFor="sev">Severity</label>
                  <select id="sev" className="select" style={{ width: 160 }} value={severity} onChange={(e) => setSeverity(e.target.value)}>
                    {["low", "medium", "high", "critical"].map((s) => <option key={s} value={s}>{label(s)}</option>)}
                  </select>
                  <label className="sr-only" htmlFor="own">Owner</label>
                  <input id="own" className="input" style={{ flex: 1, minWidth: 180 }} placeholder="Owner (optional)" value={owner} onChange={(e) => setOwner(e.target.value)} />
                  <button className="btn primary" disabled={busy || (!p && !proc.process)} title={!p && !proc.process ? "Choose the process first" : undefined}
                    onClick={() => run(() => api.triage(rec.id, severity, owner.trim() || undefined, !p && proc.process ? { processId: proc.process, step: proc.step || undefined } : undefined))}>Triage</button>
                </div>
              </section>
            )}
            <section className="card">
              <div className="card-head"><h2>General</h2></div>
              <div className="card-body">
                <dl className="dl">
                  {rec.type === "issue" ? (
                    <><dt>Description</dt><dd style={{ whiteSpace: "pre-wrap" }}>{d.description || <span className="muted">No details given.</span>}</dd>
                      <dt>Process</dt><dd>{editProc ? (
                        <div>
                          <ProcessPicker value={proc} onChange={setProc} required idPrefix="ep" />
                          <div className="btn-row"><button className="btn primary" disabled={busy || !proc.process} onClick={() => run(() => api.issueProcess(rec.id, proc.process, proc.step || undefined), async () => { setEditProc(false); await reload(); })}>Save</button>
                            <button className="btn" onClick={() => setEditProc(false)}>Cancel</button></div>
                        </div>
                      ) : <>{p ? <><Link to={`/improve/catalog/${p.number}`}>{p.number} {p.name}</Link>{p.step && <span className="muted"> · Step: {p.step.name}</span>}</> : <span className="muted">Not set</span>}
                        {" "}<button className="linkish" onClick={() => { setProc({ process: p?.number ?? "", step: p?.step?.id ?? "" }); setEditProc(true); }}>{p ? "Change" : "Choose the process"}</button></>}</dd>
                      <dt>Outcome</dt><dd>{label(d.outcome)}</dd></>
                  ) : (
                    <><dt>Problem statement</dt><dd style={{ whiteSpace: "pre-wrap" }}>{d.problemStatement}</dd>
                      <dt>Source</dt><dd>{label(d.source)}</dd></>
                  )}
                  <dt>Owner</dt><dd>{rec.ownerId ?? <span className="muted">Unassigned</span>}</dd>
                  <dt>Raised</dt><dd>{new Date(rec.createdAt).toLocaleString()}</dd>
                </dl>
              </div>
            </section>
            <section className="card">
              <div className="card-head"><h2>Business Central records</h2><Link to="/ontology">How links work</Link></div>
              <div className="card-body" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                {rec.erpLinks.length ? rec.erpLinks.map((l) => (
                  <Link key={l.id} to={`/o/${l.erpEntity}/${encodeURIComponent(l.displayKey)}`} className="chip-bc" title="Open this BC record's object view">
                    <span className="k">{ERP_ENTITIES.find((e) => e.value === l.erpEntity)?.label ?? label(l.erpEntity)}</span><span className="v">{l.displayKey}</span>
                  </Link>
                )) : <span className="muted">Not linked to any BC records.</span>}
              </div>
            </section>
            {allowed.length > 0 && (
              <section className="card">
                <div className="card-head"><h2>Stage note</h2></div>
                <div className="card-body">
                  <textarea className="textarea" aria-label="Note for the next stage change" placeholder="Optional note recorded with the next stage change, e.g. “Lot L-2291 quarantined”" value={note} onChange={(e) => setNote(e.target.value)} />
                </div>
              </section>
            )}
          </div>

          <div className="stack">
            {related.length > 0 && (
              <section className="card">
                <div className="card-head"><h2>Related</h2></div>
                <div className="card-body" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                  {related.map((r) => (
                    <Link key={r.id + r.relation} to={`/r/${r.number}`} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, color: "inherit" }}>
                      <span><span className="muted" style={{ fontSize: 12 }}>{label(r.relation)}</span><br /><span className="mono" style={{ color: "var(--brand)", fontWeight: 600 }}>{r.number}</span></span>
                      <StatePill state={r.state} />
                    </Link>
                  ))}
                </div>
              </section>
            )}
            <section className="card">
              <div className="card-head"><h2>Timeline</h2></div>
              <div className="card-body">
                <ol className="timeline">
                  {[...rec.history].reverse().map((h, i) => (
                    <li key={i}>
                      <div style={{ fontWeight: 600 }}>{h.fromState ? <>{label(h.fromState)} → {label(h.toState)}</> : <>Created</>}</div>
                      {h.reason && h.reason !== "created" && <div className="muted">{h.reason}</div>}
                      <div className="when">{h.actorName ?? h.actorId} · {ago(h.at)}</div>
                    </li>
                  ))}
                </ol>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
